// IMPORTAÇÃO DAS BIBLIOTECAS ---------------------------------------------------------------------
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';



// IMPORTAÇÃO DOS COMPONENTES----------------------------------------------------------------------
import styles from './upperbar.module.css';
import UserMessages from './components/userMessages/userMessages.js';
import UserSettings from './components/userSettings/userSettings.js';
import MyApp from './components/myApp/myApp.js';
import ContactModal from './components/contactModal/contactModal.js';
// import { ReactComponent as Logo } from '../../img/logo.svg';
import { api } from '../../services/api.js';
import { socket } from '../../services/websocket.js'; // certifique-se de exportar o socket
import { useAppContext } from "../../AppContext.js";
import ComponentLoad from "../../components/componentLoad/componentLoad.js";




// IMPORTAÇÃO ICONES-------------------------------------------------------------------------------
import { IoIosSearch } from "react-icons/io";
import { BiMessageSquareDots } from "react-icons/bi";
import { LiaUserSolid } from "react-icons/lia";
import { LuRefreshCcw } from "react-icons/lu";
import { MdVerifiedUser } from "react-icons/md";



// FUNÇÕES UTILITÁRIAS ----------------------------------------------------------------------------
// Conta mensagens não lidas
const contarNaoLidas = (mensagens) => mensagens.filter(msg => !msg.read).length;



// COMPONENTE -------------------------------------------------------------------------------------
const Upperbar = ({ searchTerm, onSearch }) => {
  // VARIÁVEIS-------------------------------------------------------------------------------------
  const [menuAberto, setMenuAberto] = useState(false); // estado para abrir e fechar o menu de usuário
  const menuRef = useRef(null); // estado para fechar o menu ao clicar fora
  const [isSyncing, setIsSyncing] = useState("false"); // estado para animação de sincronização
  const [qtdMsg, setQtdMsg] = useState(0); // estado para a quantidade de mensagens não lidas
  const [showMessages, setShowMessages] = useState(false); // estado para abrir e fechar o modal de mensagens
  const [showSettings, setShowSettings] = useState(false); // estado para abrir e fechar o modal de configurações de usuário
  const [showMyApp, setShowMyApp] = useState(false); // estado para abrir e fechar o modal de "Meu App"
  const [showContact, setShowContact] = useState(false); // estado para abrir o modal "Fale conosco"
  const [messages, setMessages] = useState([]); // mensagens do usuário baixadas da api
  const [errorLoadMsg, setErrorLoadMsg] = useState(false) // estado para mostrar mensagem de erro de requisição na caixa de mensagem
  const [loading, setLoading] = useState(false); // Estado para controlar load durante o logout
  const navigate = useNavigate(); // usado para navegação
  const navRef = useRef(null); // ref para inserir borda na barra de navegação caso rolar notas
  const { t } = useTranslation(); // hook para traduzir
  const { isApp } = useAppContext(); // define se está sendo acessado pelo app ou pelo site



  // EFEITOS GERAIS--------------------------------------------------------------------------------
  // Escutar evento de sincronização do aplicativo
  useEffect(() => {
    const handleSync = (e) => setIsSyncing(e.detail);
    window.addEventListener('app-syncing', handleSync);
    return () => window.removeEventListener('app-syncing', handleSync);
  }, []);

  // Fecha menu ao clicar fora do menu de configurações do usuário
  useEffect(() => {
    function handleClickFora(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuAberto(false);
      }
    }
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);

  // Adiciona classe 'scrolled' na nav ao rolar a página para aparecer borda inferior
  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 0) {
          navRef.current.classList.add(styles.scrolled);
        } else {
          navRef.current.classList.remove(styles.scrolled);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  // FUNÇÕES PARA MENSAGENS DO USUÁRIO-------------------------------------------------------------
  // Verifica mensagens do usuário
  const verificarMensagens = async () => {
    try {
      const response = await api.get('/messages/read');
      if (Array.isArray(response.data)) {
        const naoLidas = contarNaoLidas(response.data);
        setMessages(response.data);
        setQtdMsg(naoLidas);
      } else {
        setMessages([]);
        setQtdMsg(0);
      }
    } catch (error) {
      setErrorLoadMsg(true);
      console.error(error.response.data.msg || 'Error verifying messages:', error)
      setMessages([]);
      setQtdMsg(0);
    }
  };

  // Chamada inicial e atualização periódica
  useEffect(() => {
    verificarMensagens();
    // será reajustado
    // const interval = setInterval(verificarMensagens, 300000); // a cada 5 minutos
    // return () => clearInterval(interval);
  }, []);

  // Marcar mensagem como lida
  const marcarComoLida = async (msg) => {
    if (msg.read) return;
    const id = msg._id;
    try {
      await api.post(`/messages/update/${id}`);
      setMessages(prevMsgs => {
        const atualizadas = prevMsgs.map(m =>
          m._id === id ? { ...m, read: true } : m
        );
        setQtdMsg(contarNaoLidas(atualizadas));
        return atualizadas;
      });
    } catch (error) {
      console.error(error.response.data.msg || 'Error marking message as read:', error)
    }
  };



  // FUNÇÃO PARA LOGOUT DO USUÁRIO-----------------------------------------------------------------
  // Logout
  const sair = async () => {
    setLoading(true);
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('contConectado');
      localStorage.removeItem('searchTerm'); // Limpa o termo de busca salvo no localStorage para evitar que o filtro permaneça após logout
      window.Android.clearCookies(); // Limpa cookies no WebView do Android (certifique-se de ter essa função implementada na camada nativa)
      // desconectar o websocket
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close(1000, 'Logout'); // 1000 = código de encerramento normal
      }
      // console.log(response.data.msg); // para desenvolvimento
    } catch (error) {
      // console.error(error.response.data.msg || 'Erro ao fazer logout:', error) // para desenvolvimento
    } finally {
      setTimeout(() => {
        navigate('/');
      }, 1500);
    }
  };



  // BUSCAR O ÚLTIMO TERMO DE BUSCA NO LOCALSTORAGE PARA MANTER O FILTRO MESMO APÓS FECHAR O APP
  useEffect(() => {
    const termoSalvo = localStorage.getItem('searchTerm') || '';
    onSearch(termoSalvo);
    // foca no elemento de input da barra de pesquisa ao carregar o componente
    const inputPesquisa = document.getElementById('barrPesc_pesquisa');
    if (inputPesquisa && termoSalvo !== '') {
      inputPesquisa.focus();
    }
  }, [onSearch]);



  // HTML DO COMPONENTE----------------------------------------------------------------------------
  return (
    <section ref={navRef} className={`${styles.content} ${isApp ? styles.navApp : styles.navWeb}`}>
      {/* barra de navegação */}
      <nav>
        {/* animação de nuvens */}
        <div className={styles.scenery}>
          {/* <svg className={`${styles.cloud} ${styles.cloud1}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.5 19C19.9853 19 22 16.9853 22 14.5C22 12.1325 20.1772 10.2052 17.854 10.0146C17.4338 6.64333 14.5683 4 11 4C7.13401 4 4 7.13401 4 11C4 11.2033 4.00863 11.4046 4.02549 11.6033C2.30877 12.1264 1 13.7226 1 15.5C1 17.9853 3.01472 20 5.5 20H17.5Z" />
          </svg>
          <svg className={`${styles.cloud} ${styles.cloud2}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.5 19C19.9853 19 22 16.9853 22 14.5C22 12.1325 20.1772 10.2052 17.854 10.0146C17.4338 6.64333 14.5683 4 11 4C7.13401 4 4 7.13401 4 11C4 11.2033 4.00863 11.4046 4.02549 11.6033C2.30877 12.1264 1 13.7226 1 15.5C1 17.9853 3.01472 20 5.5 20H17.5Z" />
          </svg>
          <svg className={`${styles.cloud} ${styles.cloud3}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.5 19C19.9853 19 22 16.9853 22 14.5C22 12.1325 20.1772 10.2052 17.854 10.0146C17.4338 6.64333 14.5683 4 11 4C7.13401 4 4 7.13401 4 11C4 11.2033 4.00863 11.4046 4.02549 11.6033C2.30877 12.1264 1 13.7226 1 15.5C1 17.9853 3.01472 20 5.5 20H17.5Z" />
          </svg>
          <svg className={`${styles.cloud} ${styles.cloud4}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.5 19C19.9853 19 22 16.9853 22 14.5C22 12.1325 20.1772 10.2052 17.854 10.0146C17.4338 6.64333 14.5683 4 11 4C7.13401 4 4 7.13401 4 11C4 11.2033 4.00863 11.4046 4.02549 11.6033C2.30877 12.1264 1 13.7226 1 15.5C1 17.9853 3.01472 20 5.5 20H17.5Z" />
          </svg> */}

          {/* <div className={styles.birdContainer}>
            <svg className={`${styles.birdSvg} ${styles.birdSvg1}`} viewBox="0 0 100 50" width="60" height="30" fill="none" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
              <path className={styles.birdWings} d="M 10 30 Q 30 10 50 30 Q 70 10 90 30" />
            </svg>
          </div>
          <div className={`${styles.birdContainer} ${styles.birdContainer2}`}>
            <svg className={`${styles.birdSvg} ${styles.birdSvg2}`} viewBox="0 0 100 50" width="45" height="22" fill="none" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
              <path className={styles.birdWings} d="M 10 30 Q 30 10 50 30 Q 70 10 90 30" />
            </svg>
          </div> */}

        </div>

        {/* logo */}
        {/* <div className={styles.logo}>
          <Logo className={styles.logoSvg} />
        </div> */}

        {/* nome do aplicatico */}
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>tS notes</h2>
        </div>

        {/* componentes do barra de navegação */}
        <div className={styles.navContent}>
          {/* botão de Atualizar */}
          <p className={styles.navlink} onClick={() => window.location.reload()}>
            <LuRefreshCcw className={`${styles.icon} ${isSyncing === "true" ? styles.syncingIcon : isSyncing === "erro" ? styles.errorIcon : ''}`} title={t('Refresh')} />
          </p>

          {/* botão de mensagens */}
          <p className={styles.navlink} onClick={() => setShowMessages(true)}>
            <BiMessageSquareDots className={styles.icon} title={t('messages')} />
            {qtdMsg > 0 && <span className={styles.badge}>{qtdMsg}</span>}
          </p>

          {/* modal de mensagens */}
          <UserMessages
            messages={messages}
            open={showMessages}
            onClose={() => setShowMessages(false)}
            marcarComoLida={marcarComoLida}
            errorLoadMsg={errorLoadMsg}
          />

          {/* separador */}
          <span className={styles.divider}></span>

          {/* menu de configurações */}
          <div className={styles.profile} ref={menuRef}>
            <LiaUserSolid
              className={styles.icon}
              onClick={() => setMenuAberto(!menuAberto)}
              style={{ cursor: 'pointer' }}
              aria-haspopup="true"
              aria-expanded={menuAberto}
              title={t('Settings')}
            />
            {menuAberto && (
              <ul className={styles.dropdownMenu}>
                <li onClick={() => setShowSettings(true)}>{t('Settings')}</li>
                {localStorage.getItem('typeUser') === 'local' && (
                  <>
                    <li className={styles.registerBtn} onClick={() => setShowSettings(true)}>
                      <MdVerifiedUser size={18} />
                      {t('Register')}
                      <span className={styles.registerBadge}>{t('Free!')}</span>
                    </li>
                    <li onClick={sair}>{t('Login with my account')}</li>
                  </>
                )}

                <li onClick={() => setShowMyApp(true)}>{t('My App')}</li>
                <li onClick={() => { setShowContact(true); setMenuAberto(false); }}>{t('Contact Us')}</li>
                <li onClick={sair}>{t('Logout')}</li>
              </ul>
            )}
          </div>

          {/* configurações de usuário */}
          {showSettings && (
            <UserSettings
              onClose={() => setShowSettings(false)}
              reloadMessages={verificarMensagens}
            />
          )}

          {/* modal Meu App */}
          {showMyApp && (
            <MyApp
              open={showMyApp}
              onClose={() => setShowMyApp(false)}
              errorLoadMsg={errorLoadMsg}
            />
          )}

          {/* modal Fale conosco */}
          {showContact && (
            <ContactModal
              open={showContact}
              onClose={() => setShowContact(false)}
            />
          )}

        </div>
      </nav>

      {/* painel de busca e utilidades */}
      <div className={styles.searchContainer}>
        <form action="#">
          <div className={styles.formGroup}>
            <input
              aria-label={t('search')}
              id="barrPesc_pesquisa"
              type="text"
              placeholder={t('search')}
              value={searchTerm}
              onChange={e => {
                onSearch(e.target.value);
                localStorage.setItem('searchTerm', e.target.value);
              }}
            />
            <IoIosSearch className={styles.icon} />
          </div>
        </form>
      </div>

      {/* MODAL PARA AGUARDAR LOGOUT DO USUÁRIO */}
      <div>
        {loading && (
          <div className={styles.modalLoading}>
            <ComponentLoad />
          </div>
        )}
      </div>
    </section>
  );
};

export default Upperbar;